// @ts-nocheck
/**
 * Corporate Order Form Handler
 * Manages file uploads and form submission for corporate orders
 */

class CorporateOrderForm {
  constructor() {
    this.form = document.getElementById('corporate-order-form');
    this.uploadedFiles = {
      orderFile: null,
      logo1: null,
      logo2: null
    };
    
    if (this.form) {
      this.init();
    }
  }

  init() {
    this.setupFileUploads();
    this.setupFormSubmission();
    this.setupFormValidation();
  }

  setupFileUploads() {
    // Setup file upload handlers
    this.setupFileUpload('order-file-upload', 'orderFile', ['.xlsx', '.xls'], 5 * 1024 * 1024); // 5MB for Excel
    this.setupFileUpload('logo-upload-1', 'logo1', ['.png', '.jpg', '.jpeg', '.pdf'], 5 * 1024 * 1024); // 5MB for images
    this.setupFileUpload('logo-upload-2', 'logo2', ['.png', '.jpg', '.jpeg', '.pdf'], 5 * 1024 * 1024);
  }

  setupFileUpload(uploadId, fileKey, allowedExtensions, maxSize) {
    const wrapper = document.querySelector(`[data-upload-id="${uploadId}"]`);
    if (!wrapper) return;

    const input = wrapper.querySelector('.file-upload-input');
    const label = wrapper.querySelector('.file-upload-label');
    const content = wrapper.querySelector('.file-upload-content');
    const preview = wrapper.querySelector('.file-upload-preview');
    const errorDiv = wrapper.querySelector('.file-upload-error');

    // Drag and drop handlers
    label.addEventListener('dragover', (e) => {
      e.preventDefault();
      content.style.borderColor = '#7b6ae0';
      content.style.backgroundColor = '#f9f5ff';
    });

    label.addEventListener('dragleave', (e) => {
      e.preventDefault();
      content.style.borderColor = '#e9eaeb';
      content.style.backgroundColor = '#ffffff';
    });

    label.addEventListener('drop', (e) => {
      e.preventDefault();
      content.style.borderColor = '#e9eaeb';
      content.style.backgroundColor = '#ffffff';
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFileSelect(files[0], fileKey, allowedExtensions, maxSize, wrapper);
      }
    });

    // File input change handler
    input.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        this.handleFileSelect(e.target.files[0], fileKey, allowedExtensions, maxSize, wrapper);
      }
    });

    // Remove file handler
    const removeBtn = wrapper.querySelector('.file-upload-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeFile(fileKey, wrapper);
      });
    }
  }

  async handleFileSelect(file, fileKey, allowedExtensions, maxSize, wrapper) {
    const errorDiv = wrapper.querySelector('.file-upload-error');
    
    // Validate file
    const validation = this.validateFile(file, allowedExtensions, maxSize);
    if (!validation.valid) {
      this.showError(errorDiv, validation.error);
      return;
    }

    // Hide error if any
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }

    // Store file
    this.uploadedFiles[fileKey] = file;

    // Show preview
    this.showFilePreview(file, wrapper);

    // Dispatch event for form validation
    document.dispatchEvent(new CustomEvent('fileUploaded', { detail: { fileKey } }));
  }

  validateFile(file, allowedExtensions, maxSize) {
    // Check file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. Allowed: ${allowedExtensions.join(', ')}`
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `File too large. Maximum size: ${maxSizeMB}MB`
      };
    }

    return { valid: true };
  }


  showFilePreview(file, wrapper) {
    const content = wrapper.querySelector('.file-upload-content');
    const preview = wrapper.querySelector('.file-upload-preview');
    const filename = wrapper.querySelector('.file-upload-filename');

    if (content) content.style.display = 'none';
    if (preview) {
      preview.style.display = 'flex';
      if (filename) {
        filename.textContent = this.truncateFileName(file.name, 30);
      }
    }
  }

  removeFile(fileKey, wrapper) {
    const content = wrapper.querySelector('.file-upload-content');
    const preview = wrapper.querySelector('.file-upload-preview');
    const input = wrapper.querySelector('.file-upload-input');

    // Clear stored file
    this.uploadedFiles[fileKey] = null;

    // Reset input
    if (input) input.value = '';

    // Show upload UI, hide preview
    if (content) content.style.display = 'flex';
    if (preview) preview.style.display = 'none';

    // Dispatch event for form validation
    document.dispatchEvent(new CustomEvent('fileRemoved', { detail: { fileKey } }));
  }

  showError(errorDiv, message) {
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
      
      setTimeout(() => {
        errorDiv.style.display = 'none';
      }, 5000);
    }
  }

  truncateFileName(name, maxLength) {
    if (name.length <= maxLength) return name;
    
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
    
    return `${truncatedName}.${extension}`;
  }

  setupFormValidation() {
    // Real-time validation for submit button state
    const submitBtn = document.getElementById('submit-button');
    const errorDiv = document.getElementById('form-error');
    
    // Check if form is valid and enable/disable submit button
    const validateForm = () => {
      const hasOrderFile = !!this.uploadedFiles.orderFile;
      const name = document.getElementById('customer-name');
      const email = document.getElementById('customer-email');
      const fileReview = document.getElementById('file-review');
      const invoiceAgreement = document.getElementById('invoice-agreement');
      
      // Clear any previous errors
      if (errorDiv) {
        errorDiv.style.display = 'none';
      }

      // Enable submit button only if all required fields are filled
      const isValid = hasOrderFile && 
        name?.value.trim() && 
        email?.value.trim() &&
        fileReview?.checked &&
        invoiceAgreement?.checked;

      if (submitBtn) {
        submitBtn.disabled = !isValid;
      }
    };

    // Listen to file upload changes
    document.addEventListener('fileUploaded', validateForm);
    document.addEventListener('fileRemoved', validateForm);
    
    // Listen to form input changes
    const formInputs = this.form.querySelectorAll('input, textarea');
    formInputs.forEach(input => {
      input.addEventListener('input', validateForm);
      input.addEventListener('change', validateForm);
    });
    
    // Initial validation
    validateForm();
  }

  setupFormSubmission() {
    let isSubmitting = false;

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // Prevent duplicate submissions
      if (isSubmitting) {
        return;
      }

      const errorDiv = document.getElementById('form-error');
      const submitBtn = document.getElementById('submit-button');
      const originalButtonText = submitBtn ? submitBtn.textContent : 'submit for review';

      // Clear previous errors
      if (errorDiv) {
        errorDiv.style.display = 'none';
      }

      // Validate required files
      if (!this.uploadedFiles.orderFile) {
        this.showFormError('Please upload your order file (XLSX, or XLS) before submitting.');
        return;
      }

      // Validate logo consent if logos are uploaded
      const hasLogos = this.uploadedFiles.logo1 || this.uploadedFiles.logo2;
      const artworkConsent = document.getElementById('artwork-rights');
      
      if (hasLogos && artworkConsent && !artworkConsent.checked) {
        this.showFormError('Please confirm you have the rights to use the uploaded artwork.');
        return;
      }

      // Validate required form fields
      const name = document.getElementById('customer-name');
      const email = document.getElementById('customer-email');

      if (!name || !name.value.trim()) {
        this.showFormError('Please enter your name.');
        return;
      }

      if (!email || !email.value.trim()) {
        this.showFormError('Please enter your email address.');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.value.trim())) {
        this.showFormError('Please enter a valid email address.');
        return;
      }

      // Validate required checkboxes
      const fileReview = document.getElementById('file-review');
      const invoiceAgreement = document.getElementById('invoice-agreement');

      if (fileReview && !fileReview.checked) {
        this.showFormError('Please confirm you have reviewed your file.');
        return;
      }

      if (invoiceAgreement && !invoiceAgreement.checked) {
        this.showFormError('Please confirm you understand the invoice terms.');
        return;
      }
      
      // Get form data
      const formData = new FormData(this.form);
      
      // Add files to form data
      if (this.uploadedFiles.orderFile) {
        formData.append('order_file', this.uploadedFiles.orderFile);
      }
      if (this.uploadedFiles.logo1) {
        formData.append('logo_1', this.uploadedFiles.logo1);
      }
      if (this.uploadedFiles.logo2) {
        formData.append('logo_2', this.uploadedFiles.logo2);
      }

      // Disable submit button and set submitting flag
      isSubmitting = true;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
      }

      try {
        const response = await this.submitOrder(formData);
        
        if (response.success) {
          this.hidePageContent();
          this.showSuccessMessage(response.data);
          // Don't reset form - keep it hidden
        } else {
          throw new Error(response.message || 'Submission failed');
        }
      } catch (error) {
        console.error('Form submission error:', error);
        
        if (error.type === 'validation' && error.errors) {
          this.showValidationErrors(error.errors, error.message);
        } else {
          const errorMessage = error.message || 'There was an error submitting your order. Please try again.';
          this.showFormError(errorMessage);
        }
        
        // Reset submitting flag and re-enable button
        isSubmitting = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalButtonText;
        }
      }
    });
  }

  hidePageContent() {
    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      mainContent.querySelectorAll('.shopify-section:not(.corporate-order-steps)').forEach(section => {
        section.style.display = 'none';
      });
      mainContent.querySelectorAll('.shopify-section.corporate-order-steps .corporate-step').forEach(step => {
        step.style.display = 'none';
      });
    }
  }

  showFormError(message) {
    const errorDiv = document.getElementById('form-error');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
      errorDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  showValidationErrors(errors, generalMessage) {
    const errorDiv = document.getElementById('form-error');
    if (!errorDiv) return;

    const errorsByMessage = {};
    errors.forEach(error => {
      const msg = error.message || 'Unknown error';
      if (!errorsByMessage[msg]) {
        errorsByMessage[msg] = [];
      }
      errorsByMessage[msg].push(error.row_number);
    });

    // Build error message
    let errorHTML = '<div style="text-align: left;">';
    
    if (generalMessage) {
      errorHTML += `<p style="margin: 0 0 12px 0; font-weight: 600;">${generalMessage}</p>`;
    } else {
      errorHTML += '<p style="margin: 0 0 12px 0; font-weight: 600;">Please fix the following errors in your order file:</p>';
    }
    
    errorHTML += '<ul style="margin: 0; padding-left: 20px; list-style: disc;">';
    
    Object.keys(errorsByMessage).forEach(message => {
      const rows = errorsByMessage[message];
      const rowsText = rows.length > 5 
        ? `rows ${rows.slice(0, 5).join(', ')} and ${rows.length - 5} more`
        : `row${rows.length > 1 ? 's' : ''} ${rows.join(', ')}`;
      
      errorHTML += `<li style="margin-bottom: 6px;"><strong>${message}</strong> (${rowsText})</li>`;
    });
    
    errorHTML += '</ul></div>';

    errorDiv.innerHTML = errorHTML;
    errorDiv.style.display = 'block';
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async submitOrder(formData) {
    // Create new FormData with the correct field names for the API
    const apiFormData = new FormData();
    
    // Add form fields with API-expected names
    apiFormData.append('name', formData.get('name') || '');
    apiFormData.append('email', formData.get('email') || '');
    apiFormData.append('company', formData.get('company') || '');
    apiFormData.append('instructions', formData.get('instructions') || '');
    
    // Add files with API-expected names
    if (this.uploadedFiles.orderFile) {
      apiFormData.append('file', this.uploadedFiles.orderFile);
    }
    if (this.uploadedFiles.logo1) {
      apiFormData.append('logo1', this.uploadedFiles.logo1);
    }
    if (this.uploadedFiles.logo2) {
      apiFormData.append('logo2', this.uploadedFiles.logo2);
    }

    try {
      const response = await fetch(`${window.globalConfig.apiUrl}corporate-orders/upload`, {
        method: 'POST',
        body: apiFormData,
        headers: {
          'X-Shop-Domain': window.location.origin
        }
      });

      const result = await response.json();

      if (!response.ok || (result && result?.success === false)) {
        // Handle error response with validation errors
        if (result.errors && Array.isArray(result.errors)) {
          throw { type: 'validation', errors: result.errors, message: result.message };
        }
        throw new Error(result.message || `API request failed with status ${response.status}`);
      }
      
      return { success: true, data: result };
    } catch (error) {
      console.error('API submission error:', error);
      throw error;
    }
  }

  showSuccessMessage(apiResponse = {}) {
    // Hide the form
    const form = document.getElementById('corporate-order-form');
    if (form) {
      form.style.display = 'none';
    }

    // Show the thank you message
    const thankYouMessage = document.getElementById('thank-you-message');
    if (thankYouMessage) {
      thankYouMessage.style.display = 'flex';
      
      let confirmationNumber = '';
      if (apiResponse.orders && Array.isArray(apiResponse.orders) && apiResponse.orders.length > 0) {
        const orderIds = apiResponse.orders.map(order => `#${order.id}`);
        confirmationNumber = orderIds.join(', ');
      } else if (apiResponse.id) {
        confirmationNumber = `#${apiResponse.id}`;
      } else {
        confirmationNumber = 'N/A';
      }
      
      const confirmationNumberEl = document.getElementById('confirmation-number');
      if (confirmationNumberEl) {
        confirmationNumberEl.textContent = confirmationNumber;
      }

      // Scroll to the thank you message
      thankYouMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  resetForm() {
    this.form.reset();
    
    // Clear all uploaded files
    Object.keys(this.uploadedFiles).forEach(key => {
      const wrapper = document.querySelector(`[data-upload-id="${key === 'orderFile' ? 'order-file-upload' : key === 'logo1' ? 'logo-upload-1' : 'logo-upload-2'}"]`);
      if (wrapper) {
        this.removeFile(key, wrapper);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CorporateOrderForm();
  });
} else {
  new CorporateOrderForm();
}
